import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Search, RefreshCw, ChevronRight, XCircle } from 'lucide-react';
import { FullPagination } from '../../components/admin/FullPagination';
import api from '../../services/api';
import { STATO_ORDINE, statoOrdine } from '../../utils/statoOrdine';

// ── Helpers ────────────────────────────────────────────────────────────
function fmt(n: number | undefined | null) {
  if (n == null) return '—';
  return `€ ${Number(n).toFixed(2).replace('.', ',')}`;
}
function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso?.slice(0, 10) ?? '—'; }
}

// ── Types & badge maps ────────────────────────────────────────────────
// Stato, data, vettore, pagamento e importo sono quelli del gestionale, gli
// stessi che vede il Cliente (``/api/orders/admin/list-unified``). "Errore" è
// un Ordine web mai arrivato su NTS. Lo stato non si cambia dall'admin.
const STATO_ERRORE = { label: 'Errore: non sul gestionale', dot: 'bg-red-500', badge: 'text-red-700 bg-red-50 border-red-200' };
const FILTRO_STATI: { value: string; label: string }[] = [
  ...Object.entries(STATO_ORDINE).map(([value, m]) => ({ value, label: m.label })),
  { value: 'errore', label: 'Errore' },
];

// Vista unificata Piattaforma + ERP (richiesta utente 24/02/2026): ogni riga
// è un Ordine web (``source: "site"``) o un ordine NTS senza corrispondenza
// sul portale (``source: "erp"``, es. telefono/manuale). Per entrambi i dati
// mostrati vengono dal gestionale.
interface UnifiedOrder {
  source: 'site' | 'erp';
  /** Il numero del gestionale (`OW/2026/00048`), quello che vede il Cliente:
   *  l'identificativo principale (INT-134). `null` se l'ordine non è su NTS. */
  numeroGestionale: string | null;
  /** Il numero web (`2026-00077`), solo per gli Ordini web. */
  numeroOrdine: string | null;
  dataOrdine: string;
  idCliente: number | null;
  ragioneSociale: string;
  email?: string | null;
  idModalitaSpedizione: number | null;
  idCondizionePagamento: number | null;
  /** IVA inclusa, `et_totdoc` (INT-130); `null` per un Ordine web mai arrivato su NTS. */
  totaleOrdine: number | null;
  idStatoOrdine: number;
  /** Stato del gestionale, come lo vede il Cliente; "errore" se mai arrivato su NTS. */
  stato: string;
  pagamento?: string | null;
  vettore?: string | null;
  dataGestionale?: string | null;
  idOrdine: number | null;
  guid: string | null;
  erpKey: { et_tipork: string; et_anno: number; et_serie: string; et_numdoc: number } | null;
  erpStatus: string | null;
}

interface ListResp<T> {
  items: T[]; total: number; page: number; limit: number;
  pages: number; hasNext: boolean; hasPrev: boolean;
}

const PAGE_SIZE = 25;

function rowKey(o: UnifiedOrder): string {
  if (o.source === 'site') return `site-${o.guid}`;
  const k = o.erpKey!;
  return `erp-${k.et_tipork}-${k.et_anno}-${k.et_serie}-${k.et_numdoc}`;
}

function detailLink(o: UnifiedOrder): string {
  if (o.source === 'site') return `/admin/ordini/piattaforma/${o.idOrdine}`;
  const k = o.erpKey!;
  return `/admin/ordini/erp/${encodeURIComponent(k.et_tipork)}/${k.et_anno}/${encodeURIComponent(k.et_serie)}/${k.et_numdoc}`;
}

// ── Componente ───────────────────────────────────────────────────────────
export default function AdminOrdersPage() {
  const [data, setData] = useState<ListResp<UnifiedOrder> | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stato, setStato] = useState<string>('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (stato !== '') params.stato_nts = stato;
      const res = await api.get('/api/orders/admin/list-unified', { params });
      setData(res.data);
    } catch {
      setData({ items: [], total: 0, page: 1, limit: PAGE_SIZE, pages: 1, hasNext: false, hasPrev: false });
    } finally { setLoading(false); }
  }, [page, search, stato]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  return (
    <div data-testid="admin-orders-page">
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-[#2B5EA7]" />
              Ordini
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Ordini dalla piattaforma DTK e dall'ERP NTS</p>
          </div>
          <button
            onClick={() => fetchOrders()}
            className="h-9 w-9 rounded-xl border flex items-center justify-center hover:bg-gray-50 transition"
            title="Aggiorna"
            data-testid="orders-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {[
              { label: 'Totale Ordini', val: data.total, color: 'text-[#2B5EA7]' },
              { label: 'In lavorazione', val: data.items.filter(o => o.stato === 'in_lavorazione').length, color: 'text-amber-600' },
              { label: 'Evasione parziale', val: data.items.filter(o => o.stato === 'evaso_parziale').length, color: 'text-teal-600' },
              { label: 'Evasi', val: data.items.filter(o => o.stato === 'evaso').length, color: 'text-cyan-600' },
              { label: 'ERP con errori', val: data.items.filter(o => o.erpStatus === 'error').length, color: 'text-red-600' },
            ].map(k => (
              <div key={k.label} className="bg-white rounded-xl border px-4 py-3">
                <div className={`text-2xl font-bold ${k.color}`}>{k.val}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white rounded-xl border p-3 sm:p-4 mb-4 flex flex-wrap gap-2 sm:gap-3 items-center">
          <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); setPage(1); }} className="flex gap-2 flex-1 min-w-[220px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="N. ordine (OW/2026/00048 o web), cliente o conto…"
                className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B5EA7]/30"
                data-testid="orders-search-input"
              />
            </div>
            <button type="submit" className="h-9 px-4 rounded-lg bg-[#2B5EA7] text-white text-sm hover:bg-[#1E4D8C] transition">Cerca</button>
          </form>
          <select
            value={stato}
            onChange={e => { setStato(e.target.value); setPage(1); }}
            className="h-9 text-sm border rounded-lg px-3 bg-white focus:outline-none"
            data-testid="orders-stato-filter"
          >
            <option value="">Tutti gli stati</option>
            {FILTRO_STATI.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          {(search || stato !== '') && (
            <button
              onClick={() => { setSearch(''); setSearchInput(''); setStato(''); setPage(1); }}
              className="h-9 px-3 rounded-lg border text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"
              data-testid="orders-clear-filters-btn"
            >
              <XCircle className="w-3.5 h-3.5" />Azzera
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-6 h-6 rounded-full border-2 border-[#2B5EA7] border-t-transparent animate-spin" /></div>
          ) : !data || data.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="orders-empty-state">
              <ShoppingCart className="w-10 h-10 mb-3 opacity-30" />
              <p className="font-medium">Nessun ordine trovato.</p>
              <p className="text-sm mt-1 text-center max-w-sm">Gli ordini dal portale B2B e dal gestionale ERP appariranno qui.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 1100 }} data-testid="orders-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky left-0 z-20 bg-gray-50" style={{ width: 150 }}>N° Ord.</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky z-20 bg-gray-50" style={{ left: 150, width: 130 }}>Data Ordine</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Cod. Cli.</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Ragione Sociale</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Vettore</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Pagamento</th>
                    <th className="text-right px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Importo IVA incl.</th>
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Stato</th>
                    <th className="text-right px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-20" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(o => {
                    const stato = o.stato === 'errore' ? STATO_ERRORE : statoOrdine(o.stato);
                    return (
                      <tr
                        key={rowKey(o)}
                        className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors group"
                        data-testid={`order-row-${rowKey(o)}`}
                      >
                        {/* Il numero del gestionale, come lo vede il Cliente; sotto
                            quello web degli ordini del portale (INT-134). */}
                        <td className="px-4 py-3 whitespace-nowrap sticky left-0 z-10 bg-white group-hover:bg-gray-50" style={{ width: 150 }}>
                          <div className="text-sm font-mono font-semibold text-[#2B5EA7]" data-testid="order-row-numero">
                            {o.numeroGestionale ?? <span className="text-muted-foreground font-normal">non sul gestionale</span>}
                          </div>
                          {o.numeroOrdine && (
                            <div className="text-[10px] font-mono text-muted-foreground" data-testid="order-row-numero-web">web {o.numeroOrdine}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap sticky z-10 bg-white group-hover:bg-gray-50" style={{ left: 150, width: 130 }}>{fmtDate(o.dataGestionale || o.dataOrdine)}</td>
                        <td className="px-4 py-3 text-sm font-mono text-muted-foreground whitespace-nowrap">{o.idCliente ?? '—'}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium whitespace-nowrap">{o.ragioneSociale || '—'}</div>
                          {o.email && <div className="text-[11px] text-muted-foreground whitespace-nowrap">{o.email}</div>}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{o.vettore || '—'}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{o.pagamento || '—'}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-right whitespace-nowrap">{o.totaleOrdine != null ? fmt(o.totaleOrdine) : '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${stato.badge}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${stato.dot}`} />{stato.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={detailLink(o)}
                            className="inline-flex items-center gap-1 text-[11px] text-[#2B5EA7] hover:underline font-medium whitespace-nowrap"
                            data-testid="order-detail-link"
                          >
                            Dettagli <ChevronRight className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {data && (
          <FullPagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            pageSize={data.limit}
            onPageChange={setPage}
            testIdPrefix="orders-pagination"
          />
        )}
      </div>
    </div>
  );
}
